import { useCallback, useEffect, useState } from 'react';

import { SavedView as TSavedView } from '@project44-models/shipment-list';

export type Tcreate = (body: TSavedView) => Promise<{ views: TSavedView[] }>;
export type Tdelete = (id: string) => Promise<{ views: TSavedView[] }>;
export type Tsort = (sortedViewIds: string[]) => Promise<void>;
export type Tupdate = (
  id: string,
  body: TSavedView,
) => Promise<{ views: TSavedView[] }>;

export interface ISavedViewsHook {
  isLoading: boolean;
  isError: boolean;
  data: { views: TSavedView[] };
  createView: Tcreate;
  deleteView: Tdelete;
  sortViews: (sortedViewIds: string[]) => Promise<void>;
  updateView: Tupdate;
}

interface ISavedViews {
  createSavedView: Tcreate;
  deleteSavedView: Tdelete;
  fetchSavedViews: () => Promise<{ views: TSavedView[] }>;
  sortSavedViews: (sortedViewIds: string[]) => Promise<void>;
  updateSavedView: Tupdate;
}

function useSavedView({
  createSavedView,
  deleteSavedView,
  fetchSavedViews,
  sortSavedViews,
  updateSavedView,
}: ISavedViews): ISavedViewsHook {
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState<{ views: TSavedView[] }>({ views: [] });

  const doFetch = useCallback(async () => {
    setIsError(false);
    setIsLoading(true);
    await fetchSavedViews()
      .then((response) => {
        setData({ views: response.views || [] });
        setIsLoading(false);
      })
      .catch(() => {
        setIsError(true);
        setIsLoading(false);
      });
  }, [fetchSavedViews]);

  const createView: Tcreate = useCallback(
    async (body) => {
      setIsError(false);
      setIsLoading(true);
      return await createSavedView(body)
        .then((response) => {
          setData({ views: data.views.concat(response.views) });
          setIsLoading(false);
          return response;
        })
        .catch(() => {
          setIsError(true);
          setIsLoading(false);
          return { views: [] };
        });
    },
    [data.views, createSavedView],
  );

  const deleteView: Tdelete = useCallback(
    async (id) => {
      setIsError(false);
      setIsLoading(true);
      return await deleteSavedView(id)
        .then((response) => {
          setData({ views: data.views.filter((view) => view.id !== id) });
          setIsLoading(false);
          return response;
        })
        .catch(() => {
          setIsError(true);
          setIsLoading(false);
          return { views: [] };
        });
    },
    [data.views, deleteSavedView],
  );

  const sortViews: Tsort = useCallback(
    async (idList) => {
      return await sortSavedViews(idList).then((response) => {
        setData({
          views: [...data.views].sort(
            (a, b) =>
              idList.indexOf(a.id as string) - idList.indexOf(b.id as string),
          ),
        });
        return response;
      });
    },
    [data.views, sortSavedViews],
  );

  const updateView: Tupdate = useCallback(
    async (id, body) => {
      setIsError(false);
      setIsLoading(true);
      return await updateSavedView(id, body)
        .then((response) => {
          setData({
            views: data.views.map((view) =>
              view.id === id ? response.views[0] : view,
            ),
          });
          setIsLoading(false);
          return response;
        })
        .catch(() => {
          setIsError(true);
          setIsLoading(false);
          return { views: [] };
        });
    },
    [data.views, updateSavedView],
  );

  useEffect(() => {
    doFetch();
  }, [doFetch]);

  return {
    createView,
    data,
    deleteView,
    isError,
    isLoading,
    sortViews,
    updateView,
  };
}

export default useSavedView;
